import { AutosaveT } from './AutosaveContext';
import { charsToMBInUtf16, getLocalStorageMB as getLocalStorageMBFromDepless } from '../../../libTs/depless';

/**
 * Calculate the total size of localStorage in MB
 * Uses the existing utility from depless.ts
 * @returns Size in megabytes (MB)
 */
export function getLocalStorageMB(): number {
  return getLocalStorageMBFromDepless();
}

/**
 * Calculate the size of a single autosave in bytes
 * @param autosave The autosave object
 * @returns Size in bytes
 */
export function getAutosaveSize(autosave: AutosaveT): number {
  try {
    // Stringify the entire autosave to get accurate character count
    const jsonString = JSON.stringify(autosave);
    // UTF-16 uses 2 bytes per character in JavaScript
    return jsonString.length * 2;
  } catch (error) {
    console.error('Error calculating autosave size:', error);
    return 0;
  }
}

/**
 * Calculate the size of a single autosave in MB
 * @param autosave The autosave object
 * @returns Size in megabytes
 */
export function getAutosaveSizeMB(autosave: AutosaveT): number {
  try {
    const jsonString = JSON.stringify(autosave);
    return charsToMBInUtf16(jsonString.length);
  } catch (error) {
    console.error('Error calculating autosave size:', error);
    return 0;
  }
}

/**
 * Drop fields from an autosave payload that are byte-identical to the server's
 * "rehydratable" baseline (edit-from-folder-latest values — e.g. a carried-forward
 * genogram with a large inline image). Those fields are re-supplied by the server when
 * a draft is resumed, so storing them in localStorage just wastes the (3MB) quota — the
 * root cause of the "storage limit reached, can't create a new document" wall.
 *
 * Safety: a field the user actually edited differs from the baseline and is KEPT, so
 * unsaved edits (e.g. a newly pasted genogram) are never lost. If no baseline is given,
 * the data is returned unchanged.
 */
export function stripServerRehydratableFields<T extends Record<string, any>>(
  data: T,
  serverRehydratableFields?: Record<string, any>
): T {
  if (!serverRehydratableFields || !data) return data;
  const lean: any = { ...data };
  for (const key of Object.keys(serverRehydratableFields)) {
    if (!(key in lean)) continue;
    const a = lean[key];
    const b = serverRehydratableFields[key];
    // Fast path: strings (the big genogram) compare by value via === — no multi-MB
    // stringify. Deep-compare only when refs differ (small arrays/objects like caseEvents).
    const unchanged = a === b || JSON.stringify(a) === JSON.stringify(b);
    if (unchanged) delete lean[key];
  }
  return lean;
}

/**
 * Coerce whatever was parsed from localStorage['autosaves'] into a safe AutosaveT[].
 *
 * Storage holds either the legacy bare array or the newer
 * `{ autosaves, lastTombstoneClearTime }` object. A malformed value must NOT slip
 * through — most importantly an object whose `.autosaves` is a non-array like `{}`.
 * The old guard (`parsed.autosaves || []`) returned that truthy `{}`, which then
 * crashed `.filter(...)` in the drafts UI and, because the loaded state is persisted
 * straight back, re-saved the bad shape on every load (a self-perpetuating wedge).
 * Anything that isn't a real array becomes [].
 */
export function coerceAutosaveList(parsed: unknown): AutosaveT[] {
  if (Array.isArray(parsed)) return parsed;
  if (parsed && typeof parsed === 'object' && Array.isArray((parsed as any).autosaves)) {
    return (parsed as any).autosaves;
  }
  return [];
}

/**
 * Format bytes to a human-readable string
 * @param bytes Size in bytes
 * @returns Formatted string (e.g., "1.5 KB", "2.3 MB")
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

/**
 * Get the time ago string from a timestamp
 * @param timestamp Unix timestamp in milliseconds
 * @returns Human-readable time ago string
 */
export function getTimeAgo(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days} day${days !== 1 ? 's' : ''} ago`;
  if (hours > 0) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  if (minutes > 0) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  return 'Just now';
}